import React from 'react';
import { useHistory } from 'react-router-dom';
import { Globe, ShieldCheck, CheckCircle2, Circle, Clock } from 'lucide-react';
import { STEPS, stepIndex } from './wizardConfig';

/**
 * Desktop variant of the employee survey frame.
 *
 * Unlike the mobile/tablet layout (single column inside a phone bezel), the
 * desktop version leverages the extra horizontal room to add:
 *   - a persistent left sidebar with the full step list + completion state
 *   - a richer top header showing the campaign context + language picker
 *   - a wider content column with more generous spacing
 *
 * The single-column form pattern is kept inside the content area so the
 * actual questions look and feel identical to the mobile version — only the
 * surrounding context changes.
 */
const EmployeeDesktopFrame = ({ client = 'Unlimited Group', progress = null, step = null, totalSteps = null, currentSlug = null, children }) => {
  const history = useHistory();
  const activeIndex = currentSlug ? stepIndex(currentSlug) : -1;

  return (
    <div className='w-full min-h-full flex flex-col bg-background'>
      <div className='border-b border-border bg-card'>
        <div className='max-w-[1200px] mx-auto px-8 py-4 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-accent font-display text-base font-bold'>
              {client[0]}
            </div>
            <div>
              <div className='font-display text-[15px] leading-tight'>{client}</div>
              <div className='text-[11px] text-muted-foreground uppercase tracking-[0.14em]'>
                WFH &amp; Commuting Survey · FY 2026
              </div>
            </div>
          </div>
          <div className='flex items-center gap-3'>
            {progress !== null && (
              <div className='flex items-center gap-1.5 text-[11px] text-muted-foreground'>
                <Clock size={12} strokeWidth={1.5} />
                <span className='font-mono'>~{Math.max(1, Math.round(((100 - progress) / 100) * 5))} min remaining</span>
              </div>
            )}
            <button
              type='button'
              className='flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md hover:bg-muted'>
              <Globe size={13} strokeWidth={1.5} />
              English
            </button>
          </div>
        </div>
      </div>

      <div className='flex-1 max-w-[1200px] w-full mx-auto px-8 py-8 flex gap-8'>
        <aside className='w-[240px] flex-shrink-0 hidden md:block'>
          <div className='sticky top-8'>
            <div className='text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3'>
              Progress
            </div>
            {progress !== null && (
              <div className='flex items-center gap-2 mb-5'>
                <div className='flex-1 h-1.5 rounded-full bg-muted overflow-hidden'>
                  <div className='h-full bg-primary transition-all' style={{ width: `${progress}%` }} />
                </div>
                <span className='text-[11px] font-mono text-muted-foreground'>{progress}%</span>
              </div>
            )}
            <ol className='space-y-1'>
              {STEPS.map((s, i) => {
                const isActive = i === activeIndex;
                const isComplete = activeIndex >= 0 && i < activeIndex;
                const Icon = isComplete ? CheckCircle2 : Circle;
                const canJump = isComplete;
                return (
                  <li key={s.slug}>
                    <button
                      type='button'
                      disabled={!canJump}
                      onClick={() => canJump && history.push(`/prototype/wfh-survey/employee/step/${s.slug}`)}
                      className={`w-full text-left flex items-start gap-2.5 px-2.5 py-2 rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary/10 text-foreground'
                          : canJump
                          ? 'text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer'
                          : 'text-muted-foreground/70 cursor-default'
                      }`}>
                      <Icon
                        size={14}
                        strokeWidth={1.75}
                        className={`mt-0.5 flex-shrink-0 ${
                          isComplete ? 'text-success' : isActive ? 'text-accent' : 'text-muted-foreground/60'
                        }`}
                      />
                      <div className='min-w-0 flex-1'>
                        <div className={`text-[12px] font-medium leading-tight ${isActive ? 'text-foreground' : ''}`}>
                          {s.label}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </aside>

        <main className='flex-1 min-w-0'>
          {step != null && totalSteps != null && (
            <div className='text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-2'>
              Step {step} of {totalSteps}
            </div>
          )}
          <div className='bg-card border border-border rounded-xl p-8 flex flex-col min-h-[500px]'>{children}</div>
        </main>
      </div>

      <div className='border-t border-border bg-card'>
        <div className='max-w-[1200px] mx-auto px-8 py-3 flex items-center justify-between text-[11px] text-muted-foreground'>
          <div className='flex items-center gap-1.5'>
            <ShieldCheck size={12} strokeWidth={1.5} />
            Your responses are encrypted in transit and tied to your employer only.
          </div>
          <div className='flex items-center gap-3'>
            <span>Powered by Compare Your Footprint</span>
            <span className='opacity-50'>·</span>
            <button type='button' className='hover:text-foreground transition-colors'>
              Privacy
            </button>
            <button type='button' className='hover:text-foreground transition-colors'>
              Help
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDesktopFrame;
